import { Trans, useLingui } from "@lingui/react/macro";
import Select from "@medoma/components/Select/Select";
import { useInvoicingCodes } from "@/api/Invoicing";
import { invoicingCodeTypeOptionsSchema } from "@medoma/models/invoicing";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Heading } from "@medoma/components/Heading/Heading";
import { useFormContext, type FieldValues } from "react-hook-form";
import type { IPatientType } from "@medoma/models/patients";
import NoResults from "@/components/NoResults/NoResults";
import {
  useFieldPathWithPrefix,
  useFieldErrorWithPrefix,
} from "@/forms/FormFieldPathPrefixContext";
import type { TemplateInvoicingCodesPickerFields } from "@/pages/template-manager/TemplateForm/templateFormSchema";

export const TemplateInvoicingCodesPicker = ({
  selectedPatientType,
}: {
  selectedPatientType: IPatientType;
}) => {
  const { t } = useLingui();
  const {
    formState: { errors },
    register,
  } = useFormContext<FieldValues>();

  const procedureCodesFieldName =
    useFieldPathWithPrefix<TemplateInvoicingCodesPickerFields>(
      "procedureCodes",
    );
  const productCodesFieldName =
    useFieldPathWithPrefix<TemplateInvoicingCodesPickerFields>("productCodes");
  const procedureCodesError =
    useFieldErrorWithPrefix<TemplateInvoicingCodesPickerFields>(
      errors,
      "procedureCodes",
    );
  const productCodesError =
    useFieldErrorWithPrefix<TemplateInvoicingCodesPickerFields>(
      errors,
      "productCodes",
    );

  const {
    data: invoicingCodes,
    isPending,
    isError,
  } = useInvoicingCodes({
    patientType: selectedPatientType,
    codeType: null,
  });

  if (isPending) {
    return (
      <div>
        <Heading level="h3" weight="medium" margin="bottom">
          <Trans>Fakturering</Trans>
        </Heading>
        <Loading message={t`Hämtar faktureringskoder`} />
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <Heading level="h3" weight="medium" margin="bottom">
          <Trans>Fakturering</Trans>
        </Heading>
        <ErrorMessage message={t`Kunde inte hämta faktureringskoder`} />
      </div>
    );
  }

  const filteredInvoicingCodesBySelectedPatientTypes = invoicingCodes.filter(
    (code) => selectedPatientType === code.patientType,
  );

  if (filteredInvoicingCodesBySelectedPatientTypes.length === 0) {
    return (
      <div>
        <Heading level="h3" weight="medium">
          <Trans>Åtgärd (KVÅ) och produkt</Trans>
        </Heading>
        <NoResults
          message={t`Inga åtgärds- eller produktkoder hittades för den valda patienttypen`}
        />
      </div>
    );
  }

  const procedureCodes = filteredInvoicingCodesBySelectedPatientTypes.filter(
    (code) => code.type === invoicingCodeTypeOptionsSchema.enum.procedureCode,
  );
  const productCodes = filteredInvoicingCodesBySelectedPatientTypes.filter(
    (code) => code.type === invoicingCodeTypeOptionsSchema.enum.productCode,
  );

  const hasProcedureCodes = procedureCodes.length > 0;
  const hasProductCodes = productCodes.length > 0;

  return (
    <>
      {hasProcedureCodes ? (
        <Select
          label={t`Åtgärd (KVÅ)`}
          description={t`Välj flera åtgärder genom att hålla ner Ctrl-tangenten (⌘ på Mac)`}
          multiple
          {...register(procedureCodesFieldName)}
          errorMessage={procedureCodesError}
        >
          {procedureCodes.map((procedureCode) => (
            <option key={procedureCode.id} value={procedureCode.id}>
              {`${procedureCode.code}, ${procedureCode.name}`}
            </option>
          ))}
        </Select>
      ) : null}
      {hasProductCodes ? (
        <Select
          label={t`Produkt`}
          description={t`Välj flera produkter genom att hålla ner Ctrl-tangenten (⌘ på Mac)`}
          multiple
          {...register(productCodesFieldName)}
          errorMessage={productCodesError}
        >
          {productCodes.map((productCode) => (
            <option key={productCode.id} value={productCode.id}>
              {`${productCode.code}, ${productCode.name}`}
            </option>
          ))}
        </Select>
      ) : null}
    </>
  );
};
